import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

// Modal per shared-docs/frontend-architecture.md and ux-requirements.md:
//   - aria-modal, aria-labelledby, role="dialog"
//   - Escape closes
//   - Backdrop click closes
//   - Focus trapped to the modal panel
export default function Modal({ isOpen, onClose, title, children }) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && panelRef.current) panelRef.current.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 safe-area-top safe-area-bottom"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-night-surface rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col focus:outline-none"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-night-card">
          <h2 id="modal-title" className="text-base font-semibold text-gray-900 dark:text-night-text">
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1 -mr-1 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-500 dark:text-night-muted"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
